import React, { useEffect, useState } from 'react';
import { Star, MessageSquareQuote } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import TestimonialCard from '@/components/testimonials/TestimonialCard';
import {
  CustomerType,
  Testimonial,
  fetchTestimonials,
  summarize,
} from '@/lib/testimonials';

/**
 * Public wall of APPROVED reviews with a type filter and a live summary.
 * Reads nothing that RLS would not hand to an anonymous visitor.
 */
const TestimonialWall: React.FC<{ limit?: number }> = ({ limit = 48 }) => {
  const { t } = useI18n();
  const [rows, setRows] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<CustomerType | 'all'>('all');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchTestimonials({ limit }).then((data) => {
      if (!alive) return;
      setRows(data);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [limit]);

  const stats = summarize(rows);
  const shown = filter === 'all' ? rows : rows.filter((r) => r.customer_type === filter);

  const filters: { key: CustomerType | 'all'; label: string }[] = [
    { key: 'all', label: t('testi.filterAll') },
    { key: 'homeowner', label: t('testi.typeHomeowner') },
    { key: 'contractor', label: t('testi.typeContractor') },
  ];

  if (loading) {
    return (
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-56 animate-pulse rounded-2xl border border-slate-200 bg-white" />
        ))}
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
        <MessageSquareQuote className="mx-auto h-10 w-10 text-slate-300" aria-hidden="true" />
        <h3 className="mt-3 text-lg font-bold text-slate-900">{t('testi.emptyTitle')}</h3>
        <p className="mt-1 text-sm text-slate-500">{t('testi.emptyBody')}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Summary — computed from the rows actually published, never invented. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
        <span className="inline-flex items-center gap-2 text-lg font-extrabold text-slate-900">
          <Star className="h-5 w-5 fill-amber-400 text-amber-400" aria-hidden="true" />
          {stats.average.toFixed(1)}
          <span className="text-sm font-semibold text-slate-500">
            {t('testi.avgLabel', { avg: stats.average.toFixed(1) })}
          </span>
        </span>
        <span className="text-sm font-semibold text-slate-600">
          {t('testi.countLabel', { count: stats.count })}
        </span>
        <span className="text-sm font-semibold text-slate-600">
          {t('testi.recommendLabel', { pct: stats.recommendPct })}
        </span>
      </div>

      {/* Type filter */}
      <div className="mt-6 flex flex-wrap gap-2">
        {filters.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            aria-pressed={filter === f.key}
            className={`rounded-full border px-4 py-2 text-sm font-bold transition-all ${
              filter === f.key
                ? 'border-brand-navy bg-brand-navy text-white'
                : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((item) => (
          <TestimonialCard key={item.id} item={item} />
        ))}
      </div>

      {!shown.length && (
        <p className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center text-sm text-slate-500">
          {t('testi.emptyBody')}
        </p>
      )}
    </div>
  );
};

export default TestimonialWall;
